import { useEffect, useId, useMemo, useRef, useState } from "react";
import {
  MAX_LISTS,
  WATCH_FLAGS,
  activeList,
  addSection,
  addSymbol,
  applySortToOrder,
  clearList,
  copyList,
  createList,
  deleteList,
  dropItem,
  hasSymbol,
  itemKey,
  nextSort,
  removeItem,
  renameList,
  renameSection,
  searchSymbols,
  setFlag,
  setSort,
  sortedItems,
  stepSymbol,
  switchList,
  toggleSection,
  visibleItems,
  type SymbolMatch,
  type WatchColumn,
  type WatchFlag,
  type WatchItem,
  type WatchlistDoc,
} from "@helm/core";
import { formatPercent, formatTicks, formatVolume } from "@helm/ui";
import { marketData, priceFormatOf, useMdStatus } from "../md/marketData";
import { Icon } from "../components/Icon";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { newListId, newSectionId, useWatchlistDoc, watchlistStore } from "./watchlistStore";
import { quoteRow, useMarkets } from "./useMarkets";
import "./sidebar.css";

/** Flag swatches (TradingView flagged-symbol colours). */
export const FLAG_COLORS: Record<WatchFlag, string> = {
  red: "#E5484D",
  orange: "#F76B15",
  yellow: "#F5A524",
  green: "#1FA37A",
  cyan: "#12A5C4",
  blue: "#3B82F6",
  purple: "#A855F7",
};

const COLS: { key: WatchColumn; label: string; title: string; num: boolean }[] = [
  { key: "symbol", label: "Symbol", title: "Sort by symbol", num: false },
  { key: "last", label: "Last", title: "Last trade price", num: true },
  {
    key: "chg",
    label: "Chg",
    title: "Change vs the first print received (session stats not streamed yet)",
    num: true,
  },
  {
    key: "chgPct",
    label: "Chg%",
    title: "Change % vs the first print received (session stats not streamed yet)",
    num: true,
  },
  { key: "vol", label: "Vol", title: "Contracts traded since this window subscribed", num: true },
];

function valueOf(symbol: string, key: WatchColumn): number | string | null {
  if (key === "symbol") return symbol;
  const r = quoteRow(symbol);
  switch (key) {
    case "last":
      return r.lastTicks === null || r.tickPrice === null ? null : r.lastTicks * r.tickPrice;
    case "chg":
      return r.changeTicks === null || r.tickPrice === null ? null : r.changeTicks * r.tickPrice;
    case "chgPct":
      return r.changePct;
    case "vol":
      return r.trades > 0 ? r.volume : null;
  }
}

/**
 * The watchlist (TradingView-style; owner 2026-09-28): named lists, sections, drag to reorder,
 * Last / Chg / Chg% / Vol, colour flags, click a row to load the symbol, ↑/↓ to step through it,
 * column sort (within sections). Used by the right sidebar and by the Watchlist module; both read
 * the one synced `watchlist/main` store.
 *
 * - `onPick(symbol)`: what a row click / Enter / ↑↓ does (sidebar: the active chart and its link
 *   colour; module: its link group).
 * - `selected`: the symbol to highlight (what the target currently shows).
 */
export function WatchlistView({
  onPick,
  selected,
  pickHint,
  compact = false,
}: {
  onPick: ((symbol: string) => void) | null;
  selected: string | null;
  /** Row tooltip describing what a click does. */
  pickHint: string;
  /** Module use: no list menu toolbar labels (narrow panels). */
  compact?: boolean;
}): React.JSX.Element {
  const doc = useWatchlistDoc();
  const store = watchlistStore();
  const list = activeList(doc);
  const status = useMdStatus();
  const symbols = useMemo(
    () => list.items.flatMap((it) => (it.type === "symbol" ? [it.symbol] : [])),
    [list.items],
  );
  useMarkets(symbols);
  const display = visibleItems(sortedItems(list.items, list.sort, valueOf));
  const [adding, setAdding] = useState(false);
  const [cursor, setCursor] = useState<string | null>(null);
  const [drag, setDrag] = useState<{ key: string; before: string | null } | null>(null);
  const [renamingSection, setRenamingSection] = useState<string | null>(null);
  const [flagFor, setFlagFor] = useState<string | null>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const current = cursor !== null && symbols.includes(cursor) ? cursor : selected;

  const update = (f: (d: WatchlistDoc) => WatchlistDoc): void => {
    store.update(f);
  };
  const pick = (s: string): void => {
    setCursor(s);
    onPick?.(s);
  };

  const onKeyDown = (e: React.KeyboardEvent): void => {
    if ((e.target as HTMLElement).closest("input, select, textarea")) return;
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const next = stepSymbol(
        sortedItems(list.items, list.sort, valueOf),
        current,
        e.key === "ArrowDown" ? 1 : -1,
      );
      if (next !== null) {
        pick(next);
        bodyRef.current
          ?.querySelector(`[data-key="${CSS.escape(`s:${next}`)}"]`)
          ?.scrollIntoView({ block: "nearest" });
      }
    } else if (e.key === "Enter" && current !== null) {
      e.preventDefault();
      pick(current);
    } else if (
      (e.key === "Delete" || e.key === "Backspace") &&
      current !== null &&
      symbols.includes(current)
    ) {
      e.preventDefault();
      const next = stepSymbol(sortedItems(list.items, list.sort, valueOf), current, 1);
      update((d) => removeItem(d, list.id, `s:${current}`));
      setCursor(next);
    }
  };

  const onDrop = (): void => {
    if (!drag) return;
    const { key, before } = drag;
    setDrag(null);
    if (key === before) return;
    // A sorted list is frozen as shown first, so the rows stay where the user sees them.
    update((d) => dropItem(applySortToOrder(d, list.id, valueOf), list.id, key, before));
  };

  const refNote = "Chg vs the first print received (session stats not streamed yet); Vol since subscribed.";

  return (
    <div className={compact ? "wl wl-compact" : "wl"} onKeyDown={onKeyDown}>
      <div className="wl-head">
        <ListMenu
          doc={doc}
          update={update}
          onAddSection={() => {
            const id = newSectionId();
            update((d) => addSection(d, list.id, id, "New section"));
            setRenamingSection(id);
          }}
        />
        <span className="spacer" />
        <button
          type="button"
          className={adding ? "icon-btn icon-btn-xs active" : "icon-btn icon-btn-xs"}
          aria-label="Add symbol"
          aria-expanded={adding}
          title="Add symbol (search)"
          onClick={() => {
            setAdding((a) => !a);
          }}
        >
          <Icon name="plus" size={13} />
        </button>
      </div>
      {adding && (
        <SymbolSearch
          inList={(s) => hasSymbol(list, s)}
          onAdd={(s) => {
            update((d) => addSymbol(d, list.id, s));
          }}
          onClose={() => {
            setAdding(false);
          }}
        />
      )}
      <div
        ref={bodyRef}
        className="wl-body"
        tabIndex={0}
        role="grid"
        aria-label={`Watchlist ${list.name}`}
        aria-rowcount={display.length}
        onDragOver={(e) => {
          if (!drag) return;
          e.preventDefault();
          // Below the last row: drop at the end.
          const rows = bodyRef.current?.querySelectorAll<HTMLElement>("[data-key]");
          const last = rows?.[rows.length - 1];
          if (last && e.clientY > last.getBoundingClientRect().bottom) setDrag({ ...drag, before: null });
        }}
        onDrop={(e) => {
          e.preventDefault();
          onDrop();
        }}
      >
        <div className="wl-row wl-cols" role="row">
          {COLS.map((c) => {
            const on = list.sort?.key === c.key ? list.sort.dir : 0;
            return (
              <button
                key={c.key}
                type="button"
                role="columnheader"
                className={c.num ? "wl-col num" : "wl-col"}
                aria-sort={on === 1 ? "ascending" : on === -1 ? "descending" : "none"}
                title={`${c.title} — click to sort (again to reverse, a third time for your own order)`}
                onClick={() => {
                  update((d) => setSort(d, list.id, nextSort(list.sort, c.key)));
                }}
              >
                {c.label}
                {on !== 0 && <span className="wl-sort">{on === 1 ? "▲" : "▼"}</span>}
              </button>
            );
          })}
        </div>
        {display.length === 0 && (
          <div className="wl-empty muted">
            Empty list. Use + to add symbols (NQ, ES, CL, GC…); roots load the front month.
          </div>
        )}
        {display.map((it) => (
          <Row
            key={itemKey(it)}
            item={it}
            doc={doc}
            listId={list.id}
            selected={it.type === "symbol" && it.symbol === current}
            dragging={drag?.key === itemKey(it)}
            dropBefore={drag !== null && drag.before === itemKey(it) && drag.key !== itemKey(it)}
            renaming={it.type === "section" && renamingSection === it.id}
            flagOpen={it.type === "symbol" && flagFor === it.symbol}
            pickHint={pickHint}
            onPick={pick}
            onFlagOpen={(s) => {
              setFlagFor((f) => (f === s ? null : s));
            }}
            onFlag={(s, f) => {
              update((d) => setFlag(d, s, f));
              setFlagFor(null);
            }}
            onRenameDone={(title) => {
              if (it.type === "section" && title !== null)
                update((d) => renameSection(d, list.id, it.id, title));
              setRenamingSection(null);
            }}
            onRenameStart={() => {
              if (it.type === "section") setRenamingSection(it.id);
            }}
            onToggle={() => {
              if (it.type === "section") update((d) => toggleSection(d, list.id, it.id));
            }}
            onRemove={() => {
              update((d) => removeItem(d, list.id, itemKey(it)));
            }}
            onDragStart={() => {
              setDrag({ key: itemKey(it), before: itemKey(it) });
            }}
            onDragOverRow={(before) => {
              if (drag && drag.before !== before) setDrag({ ...drag, before });
            }}
            onDragEnd={() => {
              setDrag(null);
            }}
          />
        ))}
        {drag !== null && drag.before === null && <div className="wl-drop-end" />}
      </div>
      <div className="wl-foot muted" title={refNote}>
        {status === "connected"
          ? "Chg: since first print (no session stats yet)"
          : status === "off"
            ? "Prices stream once signed in."
            : "Market data is not connected."}
      </div>
    </div>
  );
}

function Row({
  item,
  doc,
  listId,
  selected,
  dragging,
  dropBefore,
  renaming,
  flagOpen,
  pickHint,
  onPick,
  onFlagOpen,
  onFlag,
  onRenameDone,
  onRenameStart,
  onToggle,
  onRemove,
  onDragStart,
  onDragOverRow,
  onDragEnd,
}: {
  item: WatchItem;
  doc: WatchlistDoc;
  listId: string;
  selected: boolean;
  dragging: boolean;
  dropBefore: boolean;
  renaming: boolean;
  flagOpen: boolean;
  pickHint: string;
  onPick: (s: string) => void;
  onFlagOpen: (s: string) => void;
  onFlag: (s: string, f: WatchFlag | null) => void;
  onRenameDone: (title: string | null) => void;
  onRenameStart: () => void;
  onToggle: () => void;
  onRemove: () => void;
  onDragStart: () => void;
  onDragOverRow: (beforeKey: string) => void;
  onDragEnd: () => void;
}): React.JSX.Element {
  const key = itemKey(item);
  const cls = ["wl-row", dragging ? "dragging" : "", dropBefore ? "drop-before" : ""];
  const dnd = {
    draggable: !renaming,
    onDragStart: (e: React.DragEvent) => {
      e.dataTransfer.effectAllowed = "move";
      e.dataTransfer.setData("application/x-helm-watch", `${listId}/${key}`);
      onDragStart();
    },
    onDragOver: (e: React.DragEvent) => {
      e.preventDefault();
      const r = e.currentTarget.getBoundingClientRect();
      // Upper half: before this row; lower half: before the next one (resolved by the next row).
      if (e.clientY < r.top + r.height / 2) onDragOverRow(key);
      else {
        const next = (e.currentTarget as HTMLElement).nextElementSibling as HTMLElement | null;
        const nk = next?.dataset["key"];
        if (nk) onDragOverRow(nk);
      }
    },
    onDragEnd,
  };
  if (item.type === "section") {
    return (
      <div className={[...cls, "wl-section"].join(" ")} data-key={key} role="row" {...dnd}>
        <button
          type="button"
          className="icon-btn icon-btn-xs wl-caret"
          aria-expanded={!item.collapsed}
          aria-label={item.collapsed ? `Expand ${item.title}` : `Collapse ${item.title}`}
          onClick={onToggle}
        >
          <span className={item.collapsed ? "wl-chev closed" : "wl-chev"} aria-hidden="true">
            ▾
          </span>
        </button>
        {renaming ? (
          <InlineRename initial={item.title} onDone={onRenameDone} label="Section name" />
        ) : (
          <span
            className="wl-section-title"
            title="Double-click to rename; drag to move"
            onDoubleClick={onRenameStart}
          >
            {item.title}
          </span>
        )}
        <span className="spacer" />
        <button
          type="button"
          className="icon-btn icon-btn-xs wl-x"
          aria-label={`Remove section ${item.title} (its symbols stay)`}
          title="Remove section (its symbols stay)"
          onClick={onRemove}
        >
          <Icon name="close" size={11} />
        </button>
      </div>
    );
  }
  const s = item.symbol;
  const m = marketData.get(s);
  const f = priceFormatOf(m?.info ?? null);
  const r = quoteRow(s);
  const tone =
    r.changeTicks === null ? "none" : r.changeTicks > 0 ? "pos" : r.changeTicks < 0 ? "neg" : "zero";
  const flag = doc.flags[s];
  return (
    <div
      className={[...cls, "wl-sym", selected ? "row-selected" : ""].join(" ")}
      data-key={key}
      role="row"
      aria-selected={selected}
      title={`${pickHint} · drag to reorder`}
      onClick={() => {
        onPick(s);
      }}
      {...dnd}
    >
      <span className="wl-cell wl-symcell" role="gridcell">
        <button
          type="button"
          className={flag ? "wl-flag on" : "wl-flag"}
          style={flag ? { color: FLAG_COLORS[flag] } : undefined}
          aria-label={flag ? `${s} flagged ${flag} — change flag` : `Flag ${s}`}
          aria-expanded={flagOpen}
          title="Colour flag"
          onClick={(e) => {
            e.stopPropagation();
            onFlagOpen(s);
          }}
        >
          <svg width="10" height="12" viewBox="0 0 10 12" aria-hidden="true">
            <path
              d="M1 1v10M1 1h7l-2 2.5L8 6H1"
              fill={flag ? "currentColor" : "none"}
              stroke="currentColor"
            />
          </svg>
        </button>
        {flagOpen && (
          <span
            className="wl-flags"
            role="menu"
            aria-label={`Flag ${s}`}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            {WATCH_FLAGS.map((c) => (
              <button
                key={c}
                type="button"
                role="menuitemradio"
                aria-checked={flag === c}
                aria-label={c}
                title={c}
                className="wl-swatch"
                style={{ background: FLAG_COLORS[c] }}
                onClick={() => {
                  onFlag(s, c);
                }}
              />
            ))}
            <button
              type="button"
              role="menuitemradio"
              aria-checked={!flag}
              className="wl-swatch none"
              aria-label="No flag"
              title="No flag"
              onClick={() => {
                onFlag(s, null);
              }}
            >
              ×
            </button>
          </span>
        )}
        <span className="sym">{s}</span>
        {m?.info && m.info.symbol !== s && <span className="muted sym-contract">{m.info.symbol}</span>}
      </span>
      <span className="wl-cell num" role="gridcell">
        {formatTicks(r.lastTicks, f)}
      </span>
      <span className={`wl-cell num tone-${tone}`} role="gridcell">
        {r.changeTicks === null
          ? "—"
          : `${r.changeTicks > 0 ? "+" : r.changeTicks < 0 ? "−" : ""}${formatTicks(Math.abs(r.changeTicks), f)}`}
      </span>
      <span className={`wl-cell num tone-${tone}`} role="gridcell">
        {r.changePct === null ? "—" : formatPercent(r.changePct)}
      </span>
      <span className="wl-cell num wl-vol" role="gridcell">
        {r.trades > 0 ? formatVolume(r.volume) : "—"}
        <button
          type="button"
          className="icon-btn icon-btn-xs wl-x"
          aria-label={`Remove ${s}`}
          title="Remove from this list (Delete)"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
        >
          <Icon name="close" size={11} />
        </button>
      </span>
    </div>
  );
}

function InlineRename({
  initial,
  onDone,
  label,
}: {
  initial: string;
  onDone: (v: string | null) => void;
  label: string;
}): React.JSX.Element {
  const ref = useRef<HTMLInputElement>(null);
  const done = useRef(false);
  useEffect(() => {
    ref.current?.focus();
    ref.current?.select();
  }, []);
  const finish = (v: string | null): void => {
    if (done.current) return;
    done.current = true;
    onDone(v);
  };
  return (
    <input
      ref={ref}
      className="input input-sm wl-rename"
      defaultValue={initial}
      aria-label={label}
      maxLength={40}
      onClick={(e) => {
        e.stopPropagation();
      }}
      onKeyDown={(e) => {
        e.stopPropagation();
        if (e.key === "Enter") finish(e.currentTarget.value);
        if (e.key === "Escape") finish(null);
      }}
      onBlur={(e) => {
        finish(e.currentTarget.value);
      }}
    />
  );
}

/** The list-name menu (TradingView): switch list, new, copy, rename, add section, clear, delete. */
function ListMenu({
  doc,
  update,
  onAddSection,
}: {
  doc: WatchlistDoc;
  update: (f: (d: WatchlistDoc) => WatchlistDoc) => void;
  onAddSection: () => void;
}): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [confirm, setConfirm] = useState<"delete" | "clear" | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);
  const list = activeList(doc);
  useEffect(() => {
    if (!open) return;
    const doc0 = boxRef.current?.ownerDocument ?? document;
    const onDown = (e: PointerEvent): void => {
      if (!boxRef.current?.contains(e.target as Node)) setOpen(false);
    };
    doc0.addEventListener("pointerdown", onDown);
    return () => {
      doc0.removeEventListener("pointerdown", onDown);
    };
  }, [open]);
  const run = (f: () => void) => () => {
    setOpen(false);
    f();
  };
  const count = list.items.filter((it) => it.type === "symbol").length;
  return (
    <div className="wl-listmenu" ref={boxRef}>
      {renaming ? (
        <InlineRename
          initial={list.name}
          label="List name"
          onDone={(v) => {
            if (v !== null) update((d) => renameList(d, list.id, v));
            setRenaming(false);
          }}
        />
      ) : (
        <button
          type="button"
          className="wl-listname"
          aria-haspopup="menu"
          aria-expanded={open}
          title="Lists: switch, new, copy, rename, sections, clear, delete (double-click to rename)"
          onClick={() => {
            setOpen((o) => !o);
          }}
          onDoubleClick={() => {
            setOpen(false);
            setRenaming(true);
          }}
        >
          <span className="wl-listname-text">{list.name}</span>
          <span className="muted wl-count">{count}</span>
          <Icon name="chevronDown" size={11} />
        </button>
      )}
      {open && (
        <div className="popover menu wl-menu" role="menu" aria-label="Watchlists">
          <div className="menu-label">Lists</div>
          {doc.lists.map((l) => (
            <button
              key={l.id}
              type="button"
              role="menuitemradio"
              aria-checked={l.id === list.id}
              className={l.id === list.id ? "menu-item active" : "menu-item"}
              onClick={run(() => {
                update((d) => switchList(d, l.id));
              })}
            >
              <Icon name="list" size={12} /> {l.name}
              <span className="muted wl-menu-count">
                {l.items.filter((it) => it.type === "symbol").length}
              </span>
            </button>
          ))}
          <div className="menu-sep" role="separator" />
          <button
            type="button"
            role="menuitem"
            className="menu-item"
            disabled={doc.lists.length >= MAX_LISTS}
            onClick={run(() => {
              update((d) => createList(d, newListId()));
              setRenaming(true);
            })}
          >
            <Icon name="plus" size={12} /> New list
          </button>
          <button
            type="button"
            role="menuitem"
            className="menu-item"
            disabled={doc.lists.length >= MAX_LISTS}
            onClick={run(() => {
              update((d) => copyList(d, list.id, newListId()));
            })}
          >
            <Icon name="copy" size={12} /> Make a copy
          </button>
          <button
            type="button"
            role="menuitem"
            className="menu-item"
            onClick={run(() => {
              setRenaming(true);
            })}
          >
            <Icon name="pencil" size={12} /> Rename
          </button>
          <button type="button" role="menuitem" className="menu-item" onClick={run(onAddSection)}>
            <Icon name="minus" size={12} /> Add section
          </button>
          <div className="menu-sep" role="separator" />
          <button
            type="button"
            role="menuitem"
            className="menu-item"
            disabled={list.items.length === 0}
            onClick={run(() => {
              setConfirm("clear");
            })}
          >
            <Icon name="close" size={12} /> Clear the list
          </button>
          <button
            type="button"
            role="menuitem"
            className="menu-item menu-danger"
            disabled={doc.lists.length <= 1}
            title={doc.lists.length <= 1 ? "The last list can't be deleted" : undefined}
            onClick={run(() => {
              setConfirm("delete");
            })}
          >
            <Icon name="trash" size={12} /> Delete list…
          </button>
        </div>
      )}
      {confirm && (
        <ConfirmDialog
          title={confirm === "delete" ? `Delete list “${list.name}”?` : `Clear list “${list.name}”?`}
          confirmLabel={confirm === "delete" ? "Delete list" : "Clear list"}
          onCancel={() => {
            setConfirm(null);
          }}
          onConfirm={() => {
            update((d) => (confirm === "delete" ? deleteList(d, list.id) : clearList(d, list.id)));
            setConfirm(null);
          }}
        >
          {confirm === "delete"
            ? "The list is removed on every device. Other lists and your flags are not affected."
            : "Every symbol and section in this list is removed, on every device."}
        </ConfirmDialog>
      )}
    </div>
  );
}

/** "+" search box over HELM's known futures (roots resolve to the front month). */
function SymbolSearch({
  inList,
  onAdd,
  onClose,
}: {
  inList: (s: string) => boolean;
  onAdd: (s: string) => void;
  onClose: () => void;
}): React.JSX.Element {
  const [q, setQ] = useState("");
  const [hi, setHi] = useState(0);
  const resultsId = useId();
  const matches: SymbolMatch[] = searchSymbols(q, 10);
  const add = (m: SymbolMatch | undefined): void => {
    if (!m) return;
    onAdd(m.symbol);
    setQ("");
    setHi(0);
  };
  return (
    <div className="wl-search">
      <input
        autoFocus
        className="input input-sm"
        value={q}
        placeholder="Add symbol: NQ, MES, crude, 6E, NQH7…"
        aria-label="Search symbols to add"
        aria-controls={resultsId}
        spellCheck={false}
        onChange={(e) => {
          setQ(e.target.value);
          setHi(0);
        }}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setHi((h) => Math.min(matches.length - 1, h + 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHi((h) => Math.max(0, h - 1));
          } else if (e.key === "Enter") {
            e.preventDefault();
            add(matches[hi]);
          } else if (e.key === "Escape") {
            onClose();
          }
        }}
      />
      <ul id={resultsId} className="wl-results" role="listbox" aria-label="Matches">
        {matches.length === 0 && <li className="muted wl-noresult">No known futures match “{q}”.</li>}
        {matches.map((m, i) => {
          const added = inList(m.symbol);
          return (
            <li
              key={`${m.kind}:${m.symbol}`}
              role="option"
              aria-selected={i === hi}
              className={i === hi ? "wl-result active" : "wl-result"}
              onPointerEnter={() => {
                setHi(i);
              }}
              onClick={() => {
                add(m);
              }}
            >
              <span className="sym">{m.symbol}</span>
              <span className="wl-result-name">
                {m.kind === "contract" ? `${m.entry.name} · contract` : m.entry.name}
              </span>
              <span className="muted wl-result-ex">{added ? "✓ in list" : m.entry.exchange}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
